import React, { useCallback, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Types
import { ShowOTComunicationProps } from './types';
import { variant } from '../../SpecialTag/types';

// Styles Components
import {
  ContainerContent,
  ContainerData,
  ContainerDescription,
  ContainerHeader,
  ContainerRefIcon,
  ContainerTitleArrow,
  InputAction,
  InputDescription,
  RefRoid,
  RightArrow,
  TagsContainer,
  TitleDate,
  TitleOrder,
} from '../MainStyledComponents';

// Components
import { DateTime } from '../../../inputs/DateTime';

// Icons
import ArrowRight from '../../../../assets/icons/triangle-right.svg';
import { SpecialTag } from '../../SpecialTag';
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';
import { ContainerOTStyled, ButtonViewDetail } from './styles';
import { dataTransferNaming, formActionNaming } from '../../../forms/ActionForms/constants';
import { formNaming as formCommunicationNaming } from '../../../forms/NewCommunicationForm/constants';
import { formAccidentNaming } from '../../../forms/AddBasicInfoAccidentForm/constants';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../../state/actions/ui/modal';
import { roidOTSelected } from '../../../../state/actions/ui/roidOTSelectedGeneralView';
import { modalIds } from '../../../modals/SuperModal/types';
import { OrderState, OrderType } from '../../../../types/orders';
import { Role } from '../../../../types/auth';
import { useAuth } from '../../../../hooks/useAuth';
import { compressSessionStorage } from '../../../../utils/sessionStorage';
import { rememberChildCardAnchor } from '../../../../utils/childCardAnchor';

export const CommunicationOT = ({
  roid,
  tag,
  date,
  capitol,
  capitols,
  subCapitol,
  subCapitolsValues,
  predefine,
  predefinedOrders,
  description,
  notes,
  pkInit,
  pkEnd,
  groupInter,
  sendStatus,
  isTransferred = false,
  id,
  communicationID,
  parentOrder,
  revalidatorFunction,
  state,
  stateParentExpeditionOrder,
  userName,
  initiallyExpanded,
}: ShowOTComunicationProps): JSX.Element => {
  const { t } = useTranslation(['common', 'forms']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;
  const [showInfo, setShowInfo] = useState(initiallyExpanded ?? false);
  const isLandscapeTablet = useIsLandscapeTablet();
  const toggleInfo = useCallback(() => setShowInfo(!showInfo), [showInfo]);

  // Handler para navegar al detalle de la comunicación
  const handleViewDetail = useCallback(() => {
    // BUG FIX: Limpiar contextos de otras entidades antes de setear el de comunicación
    sessionStorage.removeItem(formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT);
    sessionStorage.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);

    // Ancla de vuelta: al volver, `ShowOT` traerá esta tarjeta a la vista y la
    // dejará desplegada. Cubre las dos puertas — el botón «Volver» (que hace
    // PUSH) y la flecha atrás del navegador (POP).
    rememberChildCardAnchor(parentOrder?.id, roid);

    // Store navigation context before navigating
    sessionStorage.setItem(
      formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          origin: 'workorder',
          parentOrderId: parentOrder?.id,
          parentRoid: parentOrder?.roid, // Parent order ROID for breadcrumb
          parentOrderType: parentOrder?.type, // Parent order type for campaign detection
          entityRoid: roid, // Communication ROID for breadcrumb
        })
      )
    );

    // Dispatch roidOTSelected to update Redux state for breadcrumb switch case
    dispatch(roidOTSelected(OrderType.Communication, roid, communicationID));

    // Context-aware navigation: detect if parent is a campaign
    const isCampaign = parentOrder?.type === OrderType.Campaign;

    if (isCampaign && parentOrder?.id) {
      // Navigate within campaign context
      navigate(`/work-orders/campaign/${parentOrder.id}/communications/detail/${communicationID}`);
    } else {
      // Navigate to global communication route
      navigate(`/work-orders/communications/detail/${communicationID}`);
    }
  }, [communicationID, dispatch, navigate, parentOrder?.id, parentOrder?.type, roid]);

  // Handler para editar la comunicación
  const handleEditCommunication = useCallback(() => {
    // BUG FIX: Limpiar contextos de otras entidades antes de setear el de comunicación
    sessionStorage.removeItem(formAccidentNaming.ACCIDENT_NAVIGATION_CONTEXT);
    sessionStorage.removeItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);

    // Clear edit form storage
    sessionStorage.removeItem(formCommunicationNaming.EDIT_COMMUNICATION_FORM);

    // Store navigation context before navigating
    sessionStorage.setItem(
      formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT,
      compressSessionStorage(
        JSON.stringify({
          // Order de la comunicación que se abre: la edición solo usa este
          // contexto si es el de su URL (S2, auditoría 2026-11-02).
          entityId: id,
          origin: 'workorder',
          parentOrderId: parentOrder?.id,
          parentRoid: parentOrder?.roid, // Parent order ROID for breadcrumb
          parentOrderType: parentOrder?.type, // Parent order type for campaign detection
          entityRoid: roid, // Communication ROID for breadcrumb
        })
      )
    );

    // Dispatch roidOTSelected to update Redux state for breadcrumb switch case
    // Use communicationID for breadcrumb tracking, but id (Order ID) for navigation
    dispatch(roidOTSelected('edit-communication', roid, communicationID));

    // Use `id` (Order ID) not `communicationID` (Communication entity ID)
    // The EditCommunicationLoader expects Order ID to fetch data correctly

    // Context-aware navigation: detect if parent is a campaign
    const isCampaign = parentOrder?.type === OrderType.Campaign;

    if (isCampaign && parentOrder?.id) {
      // Navigate within campaign context
      navigate(`/work-orders/campaign/${parentOrder.id}/communications/edit-communication/${id}`);
    } else {
      // Navigate to global communication route
      navigate(`/work-orders/communications/edit-communication/${id}`);
    }
  }, [communicationID, dispatch, id, navigate, parentOrder?.id, parentOrder?.type, roid]);

  const openModalTransferSubOrder = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    dispatch(
      openModal(modalIds.TRANSFER_SUBORDER_MODAL, {
        namingPersistForm: dataTransferNaming.SUB_ORDER_COMMUNICATION,
        orderToTransfer: {
          id,
          type: OrderType.Communication,
          orderParent: parentOrder.id,
        },
        currentParentOrder: {
          id: parentOrder.id,
          type: parentOrder.type,
        },
        revalidatorFunction: revalidatorFunction,
      })
    );
  };
  return (
    <ContainerOTStyled data-child-roid={roid}>
      <ContainerHeader onClick={toggleInfo}>
        <TitleOrder>
          {t('orderTitles.communication')}
          <TitleDate>{` ${format(
            new Date(date),
            'dd/MM/yyyy - HH:mm'
          )}`}</TitleDate>
        </TitleOrder>
        <ContainerTitleArrow>
          <ContainerRefIcon>
            <RefRoid>{roid}</RefRoid>
            <TagsContainer>
              <SpecialTag
                variant={tag}
                sizeTag={isLandscapeTablet ? 'small' : 'default'}
              />
              {(isTransferred ||
                (state !== OrderState.Annulled &&
                  stateParentExpeditionOrder !== OrderState.Annulled &&
                  stateParentExpeditionOrder !== OrderState.End)) && (
                <SpecialTag
                  variant={isTransferred ? variant.Transferred : variant.Transfer}
                  sizeTag={isLandscapeTablet ? 'small' : 'default'}
                  onClick={
                    !isTransferred
                      ? (e) => openModalTransferSubOrder(e)
                      : undefined
                  }
                />
              )}
            </TagsContainer>
          </ContainerRefIcon>
          <RightArrow
            src={ArrowRight}
            alt={t('common:expandInfo')}
            showInfo={showInfo}
          />
        </ContainerTitleArrow>
      </ContainerHeader>
      {showInfo ? (
        <ContainerContent>
          <ContainerData>
            <DateTime
              label={t('forms:workOrder.registerDate')}
              labelHour={t('forms:workOrder.hour')}
              isDisabled={true}
              initialDate={date}
            />

            {userName && (
              <InputAction
                label={t('forms:workOrder.createdBy')}
                typeInput="normal"
                disabled={true}
                value={userName}
              />
            )}

            {pkInit && (
              <InputAction
                label={t('forms:workOrder.pkInitial')}
                typeInput="normal"
                disabled={true}
                value={pkInit}
              />
            )}
            {pkEnd && (
              <InputAction
                label={t('forms:workOrder.pkFinal')}
                typeInput="normal"
                disabled={true}
                value={pkEnd}
              />
            )}

            {groupInter && (
              <InputAction
                label={t('common:tables.headers.groupInter')}
                typeInput="normal"
                disabled={true}
                value={groupInter}
              />
            )}
            {sendStatus && (
              <InputAction
                label={t('common:tables.headers.sendStatus')}
                typeInput="normal"
                disabled={true}
                value={
                  sendStatus?.toLowerCase() === 'sent'
                    ? t('forms:communication.sent')
                    : sendStatus?.toLowerCase() === 'received'
                      ? t('forms:communication.received')
                      : sendStatus
                }
              />
            )}

            <InputAction
              label={t('forms:workOrder.predefined')}
              typeInput="normal"
              disabled={true}
              value={
                predefinedOrders?.find(
                  (predefinedOrder) => predefinedOrder.id === predefine
                )?.name
              }
            />

            <InputAction
              label={t('forms:workOrder.chapter')}
              typeInput="normal"
              disabled={true}
              value={
                capitols?.find((capitolInter) => capitolInter.id === capitol)
                  ?.name
              }
            />

            <InputAction
              label={t('forms:workOrder.subchapter')}
              typeInput="normal"
              disabled={true}
              value={
                subCapitolsValues?.find(
                  (subCapitolInter) => subCapitolInter.id === subCapitol
                )?.name
              }
            />
          </ContainerData>
          <ContainerDescription>
            <InputDescription
              label={t('forms:workOrder.description')}
              disabled={true}
              value={description}
            />
            {notes && (
              <InputDescription
                label={t('forms:workOrder.notes')}
                disabled={true}
                value={notes}
              />
            )}
            <ButtonViewDetail
              variant="edit"
              onClick={handleEditCommunication}
              type="button"
              disabled={
                state === OrderState.Annulled ||
                stateParentExpeditionOrder === OrderState.Annulled ||
                stateParentExpeditionOrder === OrderState.End ||
                currentRole === Role.Tablet
              }
            />
            <ButtonViewDetail
              variant="visibility"
              onClick={handleViewDetail}
              type="button"
            />
          </ContainerDescription>
        </ContainerContent>
      ) : null}
    </ContainerOTStyled>
  );
};
